/**
 * 8D Module API - Actions
 *
 * Database operations for containment and corrective actions (D3, D5, D6).
 * Uses mod_8d schema for module isolation.
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface Action {
  id: string;
  tenant_id: string;
  claim_id: string;
  step_number: number;
  description: string;
  responsible: string | null;
  due_date: string | null;
  status: string;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateActionInput {
  tenant_id: string;
  claim_id: string;
  step_number: number;
  description: string;
  responsible?: string | null;
  due_date?: string | null;
}

export interface ClaimForAction {
  id: string;
  tenant_id: string;
  claim_number: string;
  title: string;
  magic_token: string;
  factory?: { name: string } | null;
  supplier?: { name: string } | null;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches claim data needed for action notifications.
 */
export async function fetchClaimForAction(claimId: string): Promise<ClaimForAction | null> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .select(`
      id,
      tenant_id,
      claim_number,
      title,
      magic_token,
      factory:factory_id(name),
      supplier:suppliers(name)
    `)
    .eq('id', claimId)
    .single();

  if (error) return null;
  return data as unknown as ClaimForAction;
}

/**
 * Fetches actions for a specific claim and step.
 */
export async function fetchActionsByClaimAndStep(
  claimId: string,
  stepNumber: number
): Promise<Action[]> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('actions')
    .select('*')
    .eq('claim_id', claimId)
    .eq('step_number', stepNumber)
    .order('created_at');

  if (error) throw error;
  return data;
}

/**
 * Fetches all actions for a claim.
 */
export async function fetchActionsByClaim(claimId: string): Promise<Action[]> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('actions')
    .select('*')
    .eq('claim_id', claimId)
    .order('step_number')
    .order('created_at');

  if (error) throw error;
  return data;
}

/**
 * Creates a new action.
 */
export async function createAction(input: CreateActionInput): Promise<Action> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('actions')
    .insert({
      tenant_id: input.tenant_id,
      claim_id: input.claim_id,
      step_number: input.step_number,
      description: input.description,
      responsible: input.responsible || null,
      due_date: input.due_date || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Creates a new action for a claim, auto-fetching tenant_id.
 */
export async function createActionForClaim(
  claimId: string,
  stepNumber: number,
  action: { description: string; responsible?: string | null; due_date?: string | null }
): Promise<Action> {
  const claim = await fetchClaimForAction(claimId);
  if (!claim) throw new Error('Claim not found');

  return createAction({
    tenant_id: claim.tenant_id,
    claim_id: claimId,
    step_number: stepNumber,
    description: action.description,
    responsible: action.responsible,
    due_date: action.due_date,
  });
}

/**
 * Updates an action's status.
 */
export async function updateActionStatus(
  actionId: string,
  status: string
): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('actions')
    .update({
      status,
      completed_at: status === 'completed' ? new Date().toISOString() : null,
    })
    .eq('id', actionId);

  if (error) throw error;
}

/**
 * Marks an action as completed.
 */
export async function completeAction(actionId: string): Promise<void> {
  return updateActionStatus(actionId, 'completed');
}

/**
 * Reopens a completed action.
 */
export async function reopenAction(actionId: string): Promise<void> {
  return updateActionStatus(actionId, 'pending');
}

/**
 * Deletes an action.
 */
export async function deleteAction(actionId: string): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('actions')
    .delete()
    .eq('id', actionId);

  if (error) throw error;
}
